import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { PACKAGING_SUPPLIERS_KEY } from './packagingSuppliers'
import type { PackagingFieldCrossCheckIssueCode } from './packagingSuppliers'

export type PackagingItemIssuesDashboardGroup =
  | 'inconsistent'
  | 'missing'
  | 'non_compliant'

export type PackagingItemIssuesDashboardSectionStatus = 'ready' | 'coming_soon'

export type PackagingItemIssuesDashboardSectionId =
  | 'name_description_components'
  | 'material_recycling_stream_mismatch'
  | 'hm_subject_mismatch'
  | 'soc_subject_mismatch'
  | 'missing_components'
  | 'missing_dieline'
  | 'unreadable_or_protected_documents'
  | 'unsupported_document_file_type'
  | 'missing_hm_test_or_statement'
  | 'hm_test_deficient'
  | 'missing_soc_test_or_statement'
  | 'soc_test_deficient'
  | 'hm_above_100_mg_kg'
  | 'soc_svhc_present'

export type PackagingItemIssuesDashboardSectionItem = {
  id: string
  name: string
  sku: string | null
  supplierId: string
  supplierName: string
  issueCodes?: PackagingFieldCrossCheckIssueCode[]
  heavyMetalsVerdict?: string | null
  svhcVerdict?: string | null
}

export type PackagingItemIssuesDashboardSection = {
  id: PackagingItemIssuesDashboardSectionId
  group: PackagingItemIssuesDashboardGroup
  status: PackagingItemIssuesDashboardSectionStatus
  itemCount: number
  items: PackagingItemIssuesDashboardSectionItem[]
}

export type PackagingItemIssuesDashboardResponse = {
  sections: PackagingItemIssuesDashboardSection[]
}

export function usePackagingItemIssues() {
  return useQuery({
    queryKey: [PACKAGING_SUPPLIERS_KEY, 'packaging-item-issues'],
    queryFn: async () => {
      const { data } =
        await apiClient.get<PackagingItemIssuesDashboardResponse>(
          '/v1/packaging-suppliers/packaging-item-issues'
        )
      return data
    },
  })
}

export function packagingItemIssuesDetailTabForSection(
  sectionId: PackagingItemIssuesDashboardSectionId
): 'audits' | 'heavy-metals-audit' | 'svhc-audit' {
  if (
    sectionId === 'hm_test_deficient' ||
    sectionId === 'hm_above_100_mg_kg'
  ) {
    return 'heavy-metals-audit'
  }
  if (
    sectionId === 'soc_svhc_present' ||
    sectionId === 'soc_test_deficient'
  ) {
    return 'svhc-audit'
  }
  return 'audits'
}

export function buildPackagingItemIssuesDetailHref(params: {
  supplierId: string
  itemId: string
  sectionId: PackagingItemIssuesDashboardSectionId
}): string {
  const tab = packagingItemIssuesDetailTabForSection(params.sectionId)
  return `/packaging/suppliers/${params.supplierId}/items/${params.itemId}?tab=${tab}`
}
